import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import moment from "moment";
import { getMemberTrackerDetailApi } from "src/DAL/tracker/tracker";
import MemberTrackerForm from "./MemberTrackerForm";
import { getTrackerListMeta } from "./memberTrackersStorage";

const MUTED = "#64748b";
const INK = "#0f172a";
const PRIMARY = "var(--portal-theme-primary, #04abbb)";

/** Dedicated portal check-in screen (not the public join page). */
export default function MemberTrackerSubmitPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tracker, setTracker] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loadState, setLoadState] = useState("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadState("loading");
      try {
        const res = await getMemberTrackerDetailApi(id);
        if (cancelled) return;
        if (res?.code === 200 && res.tracker) {
          const subs = (res.submissions || []).map((row) => ({
            ...row,
            id: String(row._id || row.id),
          }));
          setTracker({
            ...res.tracker,
            _memberSubmissions: subs,
          });
          setSubmissions(subs);
          setLoadState("ready");
        } else {
          setTracker(null);
          setSubmissions([]);
          setLoadState("error");
        }
      } catch (_e) {
        if (cancelled) return;
        setTracker(null);
        setSubmissions([]);
        setLoadState("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const meta = useMemo(
    () =>
      tracker
        ? getTrackerListMeta(
            { ...tracker, _memberSubmissions: submissions },
            null
          )
        : null,
    [tracker, submissions]
  );

  const goBack = () => navigate(`/trackers/${id}`);

  if (loadState === "loading") {
    return (
      <div className="container-fluid">
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      </div>
    );
  }

  if (!tracker) {
    return (
      <div className="container-fluid">
        <button
          type="button"
          className="small-contained-button mb-3"
          onClick={() => navigate("/trackers")}
        >
          Back to Trackers
        </button>
        <Typography sx={{ fontWeight: 700, color: INK }}>
          Tracker not found
        </Typography>
        <Typography sx={{ color: MUTED, mt: 0.5 }}>
          This journey may have been removed or you no longer have access.
        </Typography>
      </div>
    );
  }

  if (!meta?.canSubmit) {
    const locked = meta?.lockedStep;
    return (
      <div className="container-fluid">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.5,
            cursor: "pointer",
            width: "fit-content",
            color: MUTED,
            "&:hover": { color: PRIMARY },
          }}
          onClick={goBack}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
            Back to {tracker.name}
          </Typography>
        </Box>
        <Box sx={{ width: "100%", py: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 22, color: INK, mb: 1 }}>
            {locked?.name || "Not open yet"}
          </Typography>
          <Typography sx={{ color: MUTED, fontSize: 14.5, lineHeight: 1.55, mb: 2.5 }}>
            {locked?.unlockAt
              ? `Come back on ${moment(locked.unlockAt).format(
                  "D MMM YYYY"
                )} to continue your journey.`
              : "There is no check-in available right now."}
          </Typography>
          <button
            type="button"
            className="small-contained-button"
            onClick={goBack}
          >
            Back to tracker
          </button>
        </Box>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
          cursor: "pointer",
          width: "fit-content",
          color: MUTED,
          "&:hover": { color: PRIMARY },
        }}
        onClick={goBack}
      >
        <ArrowBackIcon sx={{ fontSize: 18 }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
          Back to {tracker.name}
        </Typography>
      </Box>

      <Box sx={{ pb: 4, width: "100%" }}>
        <MemberTrackerForm
          tracker={tracker}
          onCancel={goBack}
          onSuccess={() => {
            navigate(`/trackers/${id}`, {
              replace: true,
              state: { submitted: true },
            });
          }}
        />
      </Box>
    </div>
  );
}
